import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  Heading,
  Pagination,
  PaginationGap,
  PaginationList,
  PaginationNext,
  PaginationPage,
  PaginationPrevious,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@a7y/tailwind-catalyst";
import type { AuditTestRun, AuditTestRunCycle } from "@a7y/types";
import {
  CacheOptionsPanel,
  DEFAULT_SKIP_CACHE_OPTIONS,
  type SkipCacheOptions,
  type SkipCacheKey,
} from "@a7y/ui/audit/shared/CacheOptionsPanel";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Clock,
  FlaskConical,
  Loader2,
  Play,
  PlayCircle,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  StatusBadge,
  PlatformIcon,
  formatRelativeTime,
  formatDuration,
  toApiCacheOptions,
} from "./shared";

const PAGE_SIZE = 30;

// =============================================================================
// MARK: MostRecentRunGrid
// =============================================================================

function MostRecentRunGrid({
  latestRuns,
  onRerun,
}: {
  latestRuns: AuditTestRun[] | undefined;
  onRerun: (appName: string, platform: "appstore" | "playstore") => void;
}) {
  const navigate = useNavigate();

  // Derive apps from latestRuns, sorted by appName asc then platform (appstore before playstore)
  const sortedRuns = useMemo(() => {
    if (!latestRuns || latestRuns.length === 0) return [];
    return [...latestRuns].sort((a, b) => {
      const nameCompare = a.appName.localeCompare(b.appName);
      if (nameCompare !== 0) return nameCompare;
      return a.platform.localeCompare(b.platform);
    });
  }, [latestRuns]);

  if (sortedRuns.length === 0) {
    return (
      <div className="mb-8 rounded-lg bg-white shadow-sm ring-1 ring-zinc-200 dark:bg-zinc-800 dark:ring-zinc-700">
        <div className="border-b border-zinc-200 px-4 py-3 dark:border-zinc-700">
          <Heading level={2}>Most Recent Run</Heading>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <FlaskConical className="mx-auto h-8 w-8 text-zinc-300 dark:text-zinc-600" />
            <Text className="mt-2 text-sm text-zinc-500">
              No recent runs. Click &quot;Trigger New Run&quot; to start.
            </Text>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8 rounded-lg bg-white shadow-sm ring-1 ring-zinc-200 dark:bg-zinc-800 dark:ring-zinc-700">
      <div className="border-b border-zinc-200 px-4 py-3 dark:border-zinc-700">
        <Heading level={2}>Most Recent Run</Heading>
      </div>
      <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
        {sortedRuns.map((run) => (
          <button
            key={run.id}
            onClick={() => navigate(`/admin/audit-testing/${run.id}`)}
            className="group relative flex items-center gap-4 rounded-lg bg-white p-4 text-left shadow-sm ring-1 ring-zinc-200 transition-colors hover:bg-zinc-50 dark:bg-zinc-800 dark:ring-zinc-700 dark:hover:bg-zinc-750"
          >
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <PlatformIcon platform={run.platform} />
                <Text className="font-medium text-zinc-900 dark:text-white">
                  {run.appName}
                </Text>
                <Text className="text-xs text-zinc-500">
                  {run.platform === "appstore" ? "iOS" : "Android"}
                </Text>
              </div>
              <div className="mt-2 flex items-center gap-3">
                <StatusBadge status={run.status} />
                <Text className="text-xs text-zinc-500">
                  {formatRelativeTime(run.completedAt ?? run.createdAt)}
                </Text>
                {run.asoReadinessScore != null && (
                  <Text className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
                    Score: {run.asoReadinessScore}
                  </Text>
                )}
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRerun(run.appName, run.platform);
              }}
              className="rounded-md p-1.5 text-zinc-400 opacity-0 transition-opacity hover:bg-zinc-100 hover:text-zinc-600 group-hover:opacity-100 dark:hover:bg-zinc-700 dark:hover:text-zinc-300"
              title={`Re-run ${run.appName} (${run.platform === "appstore" ? "iOS" : "Android"})`}
            >
              <Play className="h-4 w-4" />
            </button>
          </button>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// MARK: AuditTestingPage
// =============================================================================

export function AuditTestingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const page = Math.max(0, parseInt(searchParams.get("page") ?? "1", 10) - 1);
  const [showRunAllDialog, setShowRunAllDialog] = useState(false);
  const [cacheOptions, setCacheOptions] = useState<SkipCacheOptions>({
    ...DEFAULT_SKIP_CACHE_OPTIONS,
    all: true,
  });
  const [cacheExpanded, setCacheExpanded] = useState(false);

  // Fetch latest runs for the status grid
  const { data: latestRuns } = useQuery<AuditTestRun[]>({
    queryKey: ["audit-test-latest"],
    queryFn: () => api.getAuditTestLatest(),
    refetchInterval: (query) => {
      const runs = query.state.data;
      const hasRunning = runs?.some((r) => r.status === "running");
      return hasRunning ? 5000 : 30000;
    },
  });

  // Fetch paginated run cycles from the server
  const { data: cycleData } = useQuery<{
    cycles: AuditTestRunCycle[];
    total: number;
  }>({
    queryKey: ["audit-test-cycles", page],
    queryFn: () =>
      api.getAuditTestRunCycles({ limit: PAGE_SIZE, offset: page * PAGE_SIZE }),
    refetchInterval: (query) => {
      const cycles = query.state.data?.cycles;
      const hasRunning = cycles?.some((c) => c.status === "running");
      return hasRunning ? 5000 : 30000;
    },
  });

  const handleCacheOptionChange = (key: SkipCacheKey, value: boolean) => {
    setCacheOptions((prev) => ({ ...prev, [key]: value }));
  };

  const triggerBatchMutation = useMutation({
    mutationFn: (skipCacheOptions: SkipCacheOptions) =>
      api.triggerAuditTestBatch(toApiCacheOptions(skipCacheOptions)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["audit-test-latest"] });
      queryClient.invalidateQueries({ queryKey: ["audit-test-cycles"] });
      setShowRunAllDialog(false);
    },
  });

  const triggerSingleMutation = useMutation({
    mutationFn: ({
      appName,
      platform,
    }: {
      appName: string;
      platform: "appstore" | "playstore";
    }) =>
      api.triggerAuditTest({
        appName,
        platform,
        triggerSource: "manual",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["audit-test-latest"] });
      queryClient.invalidateQueries({ queryKey: ["audit-test-cycles"] });
    },
  });

  const totalPages = Math.ceil((cycleData?.total ?? 0) / PAGE_SIZE);

  return (
    <div className="px-4 py-0">
      <PageHeader title="Audit Testing" subtitle="Automated ASO audit engine test runs">
        <Button
          color="blue"
          onClick={() => setShowRunAllDialog(true)}
          disabled={triggerBatchMutation.isPending}
        >
          <PlayCircle className="h-4 w-4" />
          {triggerBatchMutation.isPending ? "Starting..." : "Trigger New Run"}
        </Button>
      </PageHeader>

      {/* Most Recent Run Grid */}
      <MostRecentRunGrid
        latestRuns={latestRuns}
        onRerun={(appName, platform) =>
          triggerSingleMutation.mutate({ appName, platform })
        }
      />

      {/* Run History */}
      <div className="rounded-lg bg-white shadow-sm ring-1 ring-zinc-200 dark:bg-zinc-800 dark:ring-zinc-700">
        <div className="border-b border-zinc-200 px-4 py-3 dark:border-zinc-700">
          <Heading level={2}>Run History</Heading>
        </div>
        <Table bleed>
          <TableHead>
            <TableRow>
              <TableHeader>Date</TableHeader>
              <TableHeader>Run Group</TableHeader>
              <TableHeader>Apps</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Duration</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {(cycleData?.cycles ?? []).map((cycle) => (
              <TableRow
                key={cycle.runGroupId}
                className="cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-750"
                onClick={() =>
                  navigate(`/admin/audit-testing/group/${cycle.runGroupId}`)
                }
              >
                <TableCell>
                  <Text className="text-sm text-zinc-500">
                    {new Date(cycle.createdAt).toLocaleString()}
                  </Text>
                </TableCell>
                <TableCell>
                  <Text className="font-mono text-sm font-medium text-zinc-900 dark:text-white">
                    {cycle.runGroupId.slice(0, 8)}
                  </Text>
                </TableCell>
                <TableCell>
                  <Text className="text-sm text-zinc-600 dark:text-zinc-400">
                    {cycle.appCount}
                  </Text>
                </TableCell>
                <TableCell>
                  <StatusBadge status={cycle.status} />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 text-zinc-500">
                    <Clock className="h-3 w-3" />
                    <Text className="text-sm">
                      {cycle.status === "running"
                        ? "In progress..."
                        : formatDuration(
                            cycle.completedAt
                              ? new Date(cycle.completedAt).getTime() -
                                  new Date(cycle.createdAt).getTime()
                              : undefined,
                          )}
                    </Text>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {(cycleData?.cycles ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <FlaskConical className="mx-auto h-8 w-8 text-zinc-300 dark:text-zinc-600" />
                      <Text className="mt-2 text-sm text-zinc-500">
                        No test runs yet. Click &quot;Trigger New Run&quot; to
                        start.
                      </Text>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="border-t border-zinc-200 px-4 py-3 dark:border-zinc-700">
            <Pagination>
              <PaginationPrevious
                href={page > 0 ? `?page=${page}` : null}
              />
              <PaginationList>
                {Array.from({ length: totalPages }, (_, i) => {
                  const pageNum = i + 1;
                  const current = i === page;
                  // Show first, last, current, and neighbors; gap the rest
                  if (
                    i === 0 ||
                    i === totalPages - 1 ||
                    Math.abs(i - page) <= 1
                  ) {
                    return (
                      <PaginationPage
                        key={pageNum}
                        href={`?page=${pageNum}`}
                        current={current}
                      >
                        {pageNum}
                      </PaginationPage>
                    );
                  }
                  // Show gap marker only at boundary
                  if (i === 1 || i === totalPages - 2) {
                    return <PaginationGap key={`gap-${i}`} />;
                  }
                  return null;
                })}
              </PaginationList>
              <PaginationNext
                href={
                  page < totalPages - 1 ? `?page=${page + 2}` : null
                }
              />
            </Pagination>
          </div>
        )}
      </div>

      {/* Run All Dialog */}
      <Dialog open={showRunAllDialog} onClose={() => setShowRunAllDialog(false)}>
        <DialogTitle>Run All Audit Tests</DialogTitle>
        <DialogBody>
          <Text className="mb-4 text-sm text-zinc-600 dark:text-zinc-400">
            This will trigger audit tests for all configured test apps across
            both platforms.
          </Text>
          <CacheOptionsPanel
            options={cacheOptions}
            onChange={handleCacheOptionChange}
            expanded={cacheExpanded}
            onToggleExpand={() => setCacheExpanded((prev) => !prev)}
          />
        </DialogBody>
        <DialogActions>
          <Button outline onClick={() => setShowRunAllDialog(false)}>
            Cancel
          </Button>
          <Button
            color="blue"
            onClick={() => triggerBatchMutation.mutate(cacheOptions)}
            disabled={triggerBatchMutation.isPending}
          >
            {triggerBatchMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <PlayCircle className="h-4 w-4" />
                Run All Tests
              </>
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
